import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_book, QIcon_exclamation_filled } from '@repo/icons/index'
import { useRouter } from 'next/router'
import { useState } from 'react'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsAdminLogin from '../../../../modules/hooks/common/useIsAdminLogin'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import {
  n_lesson,
  n_upcoming_lessons,
  s_lesson_style,
  s_upcoming_lessons,
} from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { track } from '../../../../modules/track'
import urls from '../../../../modules/urls'
import { locationMove } from '../../../../modules/utils'
import { COLORS, LOCATION, LOG_CATEGORY, LOG_TYPE } from '../../../../modules/vars'
import { useConfirmPopup } from '../../../basic/popup/ConfirmPopup'
import LoadingSpinner from '../../../common/LoadingSpinner'
import CreditBadge from '../../../common/buttons/CreditBadge'
import TutorAvatar from '../../../new-portal/lessons/TutorAvatar'

const LessonSettingConfirmPopup = ({
  item,
  settingInfo,
  showEnterPopup,
  setShowEnterPopup,
  handleAvatarClick,
  handleLessonStyleClick,
  inClass,
}) => {
  const { t, locale } = useTranslation()
  const { currentUser } = useCurrentUser()
  const courseTitle = (item?.course?.title ? item?.course?.title : item?.course_title) || ''
  const isAdminLogin = useIsAdminLogin()
  const id = item.id
  const router = useRouter()
  const confirmPopup = useConfirmPopup()
  const [isLoading, setIsLoading] = useState(false)
  const isWebview = useIsWebview()

  const handleLessonEnter = async () => {
    isAdminLogin ||
      track({
        location: LOCATION.LESSON,
        log_type: LOG_TYPE.LESSON.ENTER,
        log_category: LOG_CATEGORY.SPEAK,
        lesson_id: id,
        duration: item?.duration,
      })
    const response = await axiosGet(apis.lessons.enter(id))

    setIsLoading(true)
    if (response.success) {
      setIsLoading(false)
      setShowEnterPopup(false)
      locationMove(router, locale, urls.student.newPortal.lessons.classroom.as(id))
    } else {
      if (response.message) {
        setIsLoading(false)
        confirmPopup.show({
          id: 'alert-enter',
          title: t(s_upcoming_lessons.lesson_enter_modal_title),
          body: <div className="text-[14px] font-normal text-purple-500">{response.message}</div>,
          leftBtn: false,
        })
      } else {
        alert('Error occurred, page will be refreshed.')
        window.location.reload()
      }
    }
  }

  return (
    <DSModal
      id="ContentsLGTwoButtonVerticalPopup"
      isOpen={showEnterPopup}
      setIsOpen={setShowEnterPopup}
      size="xl"
      closeButton
      className="h-[596px]"
    >
      <DSModal.Title>
        {inClass ? t(n_lesson.popup_class_info.title) : t(n_lesson.popup_class_enter.title)}
      </DSModal.Title>
      <DSModal.Content className="">
        <div className="border-1 mt-[8px] rounded-[10px] border-gray-200 bg-white p-[16px] ">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CreditBadge
                duration={item.duration}
                style={{
                  paddingTop: '4px',
                  paddingBottom: '4px',
                  paddingLeft: '10px',
                  paddingRight: '10px',
                  color: '#140F33',
                  backgroundColor: 'rgb(228, 231, 244)',
                  fontWeight: 'bold',
                }}
              />
              <div className="ml-[12px] flex flex-row">
                {/* <span className="Title2-18Bd">{item.formatted_start_date}</span> */}
                {/* <div className="mx-[8px] h-[16px] w-[1px] bg-gray-200"></div> */}
                <span className="Title2-18Bd">{item.formatted_start_time}</span>
              </div>
            </div>
            <div className="Body2-14Md text-gray-500">{currentUser?.timezone}</div>
          </div>
          <div className="mt-[8px]">
            <TutorAvatar tutor={item.tutor} />
          </div>
          <div className="mt-[24px]">
            <span className="Body2-14Bd flex text-gray-700">
              <QIcon_book width="20px" height="20px" />
              <span className="ml-[4px] font-bold">
                {item.material_id || courseTitle ? `${courseTitle}` : t(n_lesson.msg_material_auto3)}
              </span>
            </span>
          </div>
        </div>
        <div className="mt-[32px]">
          <div className="flex flex-col gap-y-[16px]">
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.record)}
              </div>
              <div className="Body1-16Md ml-[16px] text-gray-800">
                {settingInfo.allow_record ? t(s_lesson_style.record.yes) : t(s_lesson_style.record.no)}
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.analysis)}
              </div>
              <div className="Body1-16Md ml-[16px] text-gray-800">
                {settingInfo.allow_caf
                  ? t(n_upcoming_lessons.lesson_style.analysis_yes)
                  : t(n_upcoming_lessons.lesson_style.analysis_no)}
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.intro)}
              </div>
              <div className="Body1-16Md ml-[16px] text-gray-800">
                {settingInfo.intro.skip_intro_user ? t(s_lesson_style.intro.skip) : t(s_lesson_style.intro.do)}
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.mode)}
              </div>
              <div className="Body1-16Md ml-[16px] flex items-center gap-x-[4px] text-gray-800">
                <span>
                  {settingInfo.lesson_mode.mode == 0
                    ? t(s_lesson_style.lesson_mode.correction)
                    : t(s_lesson_style.lesson_mode.discussion)}
                </span>
                <span>
                  {settingInfo.lesson_mode.mode == 0
                    ? settingInfo.lesson_mode.option == 0
                      ? t(s_lesson_style.mode_detail.correction.instant_short)
                      : t(s_lesson_style.mode_detail.correction.intermittent_short)
                    : settingInfo.lesson_mode.option == 0
                    ? t(s_lesson_style.mode_detail.discussion.mode55_short)
                    : t(s_lesson_style.mode_detail.discussion.mode82_short)}
                </span>
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.intensive_focus_title)}
              </div>
              <div className="Body1-16Md ml-[16px] flex gap-[4px]">
                {settingInfo.intensive_area.filter((item, i) => item.selected).length > 0
                  ? settingInfo.intensive_area
                      .filter((item, i) => item.selected)
                      .map((item, i) => (
                        <div
                          key={i}
                          className="Body2-14Bd break-words bg-gray-100 py-[4px] px-[10px] text-gray-600 rounded-[4px]"
                        >
                          {item.title}
                        </div>
                      ))
                  : '-'}
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.additional_request)}
              </div>
              <div className="Body1-16Md ml-[16px] grid grid-cols-1 gap-y-[8px]">
                {settingInfo.selectable_requests.filter((item, i) => item.selected).length > 0
                  ? settingInfo.selectable_requests
                      .filter((item, i) => item.selected)
                      .map((item, i) => (
                        <div key={i} className="Body1-16Md break-words text-gray-800">
                          {item.title}
                        </div>
                      ))
                  : '-'}
              </div>
            </div>
            <div className="flex items-start">
              <div className="Body1-16Md w-full min-w-[120px] max-w-[120px] whitespace-nowrap break-all text-gray-500">
                {t(n_lesson.popup_class.other_request)}
              </div>
              <div className="Body2-14Md ml-[16px] text-black">
                {settingInfo.other_request ? settingInfo.other_request : '-'}
              </div>
            </div>
          </div>
          {isWebview && (
            <>
              {handleLessonStyleClick && (
                <div className="mt-[32px] flex items-center rounded-[6px] bg-[#FFF3EF] p-[12px]">
                  <QIcon_exclamation_filled color="#FC997D" />
                  <div className="Body2-14Md ml-[4px]">{t(n_lesson.popup_class.enter_info)}</div>
                </div>
              )}
            </>
          )}
        </div>
      </DSModal.Content>
      {!inClass &&
        (handleLessonStyleClick ? (
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton
              size={BUTTON_SIZE.MD}
              type={BUTTON_TYPE.OUTLINE}
              customBgColor={COLORS.WHITE}
              customBorderColor={COLORS.PURPLE600}
              onClick={(e) => handleLessonStyleClick({ event: e, lessonId: item.id })}
            >
              <DSButton.Label customTxtColor={COLORS.PURPLE600} className="whitespace-nowrap">
                {t(n_lesson.popup_class.btn_lesson_style)}
              </DSButton.Label>
            </DSButton>
            <DSButton size={BUTTON_SIZE.MD} onClick={() => handleLessonEnter()}>
              <DSButton.Label className="whitespace-nowrap">
                {isLoading ? <LoadingSpinner show md primary /> : t(n_lesson.popup_class.btn_enter_lesson)}
              </DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        ) : (
          <DSModal.ButtonContainer direction="horizontal">
            <DSButton size={BUTTON_SIZE.MD} onClick={() => handleLessonEnter()} disabled={!item.enterable}>
              {' '}
              {/** ex. 수업 준비 화면에서의 수업 정보 확인 팝업 */}
              <DSButton.Label className="whitespace-nowrap">
                {isLoading ? <LoadingSpinner show md primary /> : t(n_lesson.popup_class.btn_enter_lesson)}
              </DSButton.Label>
            </DSButton>
          </DSModal.ButtonContainer>
        ))}
    </DSModal>
  )
}

export default LessonSettingConfirmPopup
